/*
 * 時間関連のユーティリティ
 **/

import Holidays from 'date-holidays';
import { resolveDateLocale } from '@/i18n/dateFormats';
import { WeekStart, resolveHolidayCountry } from '@/i18n/weekStart';

/**
 * 分数を[時間, 分]に分割して変換する
 * @param minutes 分数
 * @returns [時間, 分]
 */
export const getHoursAndMinutes = (time: number): [number, number] => {
    // Hourを取得
    const hours = Math.floor(time / 60);
    // Minuteを取得
    const minutes = time % 60;
    return [hours, minutes];
};

/**
 * 分数を[1時間23分]形式に変換する
 * @param minutes 分数
 * @returns [1時間23分]
 */
export const getHoursAndMinutesStr = (time: number): string => {
    const result: string[] = [];
    // Hourを取得
    const hours = Math.floor(time / 60);
    // Minuteを取得
    const minutes = time % 60;
    if (hours > 0) {
        result.push(`${hours}時間`);
    }
    if (minutes > 0) {
        result.push(`${minutes}分`);
    }
    return result.join('');
};

/**
 * 英語省略表記の時分を取得する
 * @param time 時間
 * @returns 英語省略表記の時分
 */
export const getEnglishHoursAndMinutes = ([hours, minutes]: [number, number]): string => {
    // 時間が0の場合は省略
    if (hours === 0) {
        return `${minutes}m`;
    } else if (minutes === 0) {
        return `${hours}h`;
    } else {
        return `${hours}h${minutes}m`;
    }
};

/**
 * 年月日から年と月日を分割
 * @param date 年月日
 * @returns 年と月日
 */
export const splitDate = (date: string): { year: string; monthDay: string } => {
    const year = date.substring(0, 4);
    const monthDay = date.substring(5);
    return { year, monthDay };
};

/**
 * 今月の祝日を取得
 * @returns 今月の祝日の日付オブジェクトリスト
 */
export const getThisMonthHolidays = (language?: string): Date[] => {
    const countryCode = resolveHolidayCountry(language);
    if (!countryCode) {
        return [];
    }

    let hd: Holidays | null = null;
    try {
        hd = new Holidays(countryCode);
    } catch {
        return [];
    }
    if (!hd) {
        return [];
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // month は1始まりで指定する必要がある

    // 現在の年月の祝日を取得
    const holidays = hd.getHolidays(year);

    // 今月の祝日だけをフィルタリング
    const thisMonthHolidays = holidays.filter((holiday) => {
        const holidayDate = new Date(holiday.date);
        if (holidayDate.getMonth() + 1 !== month) {
            return false;
        }

        const rawTypes = holiday.type;
        const types: string[] = Array.isArray(rawTypes) ? rawTypes : rawTypes ? [rawTypes] : [];
        const normalizedTypes = types
            .filter((t) => typeof t === 'string' && t.length > 0)
            .map((t) => t.toLowerCase());

        const hasPublicType = normalizedTypes.some(
            (type) => type.includes('public') || type.includes('bank') || type.includes('national'),
        );
        const hasObservanceOnly =
            normalizedTypes.length > 0 &&
            normalizedTypes.every((type) => type.includes('observance'));

        return hasPublicType && !hasObservanceOnly;
    });

    // 祝日の日付オブジェクトリストを作成
    return thisMonthHolidays.map((holiday) => new Date(holiday.date));
};

/**
 * 年月日から年を除去
 * @param date 年月日
 * @returns 年月日から年を除去した日付
 */
export const removeYearFromDate = (date: string): string => {
    return date.substring(5);
};

/**
 * 日付から曜日を取得（短縮形）
 * @param date 日付 (YYYY-MM-DD または YYYY/MM/DD)
 * @returns 曜日 (短縮形)
 */
export const getWeekday = (date: string, language: string = 'ja'): string => {
    // 年月日を分解
    const separator = date.includes('-') ? '-' : '/';
    const parts = date.split(separator);

    // 無効な形式の場合は空文字を返す
    if (parts.length !== 3) {
        return '';
    }

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JavaScriptの月は0始まり
    const day = parseInt(parts[2], 10);

    const dateObj = new Date(year, month, day);
    // 有効な日付かチェック
    if (isNaN(dateObj.getTime())) {
        return '';
    }

    // 曜日を返却（短縮形）
    const locale = resolveDateLocale(language);
    return dateObj.toLocaleDateString(locale, { weekday: 'short' });
};

/**
 * 日付から曜日を取得（完全表記）
 * @param date 日付 (YYYY-MM-DD または YYYY/MM/DD)
 * @returns 曜日 (完全表記)
 */
export const getWeekdayLong = (date: string, language: string = 'ja'): string => {
    const separator = date.includes('-') ? '-' : '/';
    const parts = date.split(separator);
    if (parts.length !== 3) return '';
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const dateObj = new Date(year, month, day);
    if (isNaN(dateObj.getTime())) return '';
    const locale = resolveDateLocale(language);
    return dateObj.toLocaleDateString(locale, { weekday: 'long' });
};

/**
 * 月日からゼロ除去
 * @param dateStr 月日
 * @returns ゼロ除去した月日
 */
export const removeZeroPaddingFromDate = (dateStr: string): string => {
    return dateStr.replace(/(\b)0?(\d{1,2})/g, (_, pre, num) => `${pre}${parseInt(num, 10)}`);
};

// 期間タイプから日付レンジを算出する
export interface DateRangeOptions {
    weekStartsOn?: WeekStart;
    baseDate?: Date;
}

export const getDateRangeForPeriod = (
    periodType: string,
    options: DateRangeOptions = {},
): { start: Date; end: Date } => {
    const weekStartsOn: WeekStart = options.weekStartsOn ?? 'mon';
    const today = options.baseDate ? new Date(options.baseDate) : new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const addDays = (d: Date, n: number) => {
        const x = new Date(d);
        x.setDate(x.getDate() + n);
        return x;
    };
    const getStartOfWeek = (base: Date): Date => {
        const d = startOfDay(base);
        const weekday = d.getDay(); // 0=Sun ... 6=Sat
        const offset = weekStartsOn === 'sun' ? weekday : (weekday + 6) % 7;
        return addDays(d, -offset);
    };

    const upper = (periodType || '').toUpperCase();
    const todayStart = startOfDay(today);
    const thisWeekStart = getStartOfWeek(todayStart);

    switch (upper) {
        case 'TODAY': {
            return { start: todayStart, end: todayStart };
        }
        case 'YESTERDAY': {
            const y = addDays(todayStart, -1);
            return { start: y, end: y };
        }
        case 'THIS_WEEK': {
            // 今週: 設定された週始まり〜週末
            const end = addDays(thisWeekStart, 6);
            return { start: thisWeekStart, end };
        }
        case 'LAST_WEEK': {
            const start = addDays(thisWeekStart, -7);
            const end = addDays(start, 6);
            return { start, end };
        }
        case 'THIS_MONTH': {
            const start = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
            const end = new Date(todayStart.getFullYear(), todayStart.getMonth() + 1, 0); // 月初〜月末
            return { start, end };
        }
        case 'LAST_MONTH': {
            const start = new Date(todayStart.getFullYear(), todayStart.getMonth() - 1, 1);
            const end = new Date(todayStart.getFullYear(), todayStart.getMonth(), 0);
            return { start, end };
        }
        case 'PAST_7_DAYS': {
            const end = addDays(todayStart, -1);
            const start = addDays(end, -6);
            return { start, end };
        }
        case 'PAST_14_DAYS': {
            const end = addDays(todayStart, -1);
            const start = addDays(end, -13);
            return { start, end };
        }
        case 'PAST_30_DAYS': {
            const end = addDays(todayStart, -1);
            const start = addDays(end, -29);
            return { start, end };
        }
        case 'PAST_3_MONTHS': {
            const end = addDays(todayStart, -1);
            const start = addDays(end, -89);
            return { start, end };
        }
        case 'PAST_6_MONTHS': {
            const end = addDays(todayStart, -1);
            const start = addDays(end, -179);
            return { start, end };
        }
        case 'PAST_12_MONTHS': {
            const end = addDays(todayStart, -1);
            const start = addDays(end, -364);
            return { start, end };
        }
        case 'THIS_YEAR': {
            const start = new Date(todayStart.getFullYear(), 0, 1);
            const end = new Date(todayStart.getFullYear(), 11, 31);
            return { start, end };
        }
        case 'LAST_YEAR': {
            const start = new Date(todayStart.getFullYear() - 1, 0, 1);
            const end = new Date(todayStart.getFullYear() - 1, 11, 31);
            return { start, end };
        }
        default: {
            // 不明な場合は先週
            const start = addDays(thisWeekStart, -7);
            const end = addDays(start, 6);
            return { start, end };
        }
    }
};
